import '@xyflow/react/dist/style.css';
import React from "react";
import type {Edge, NodeTypes} from "@xyflow/react";
import {Background, BackgroundVariant, Controls, Panel, ReactFlow, ReactFlowProvider} from "@xyflow/react";
import {Box, Stack, Typography, useTheme} from "@mui/material";
import type {DiffNode} from "~/prototypes/pages/taxonomy-canvas/approval/changeDiffGraph.ts";
import {ChangeDiffNodeView} from "~/prototypes/pages/taxonomy-canvas/approval/ChangeDiffNodeView.tsx";

const NODE_TYPES: NodeTypes = {diff: ChangeDiffNodeView};

/** A short line sample for the legend */
const LineSample: React.FC<{dashed?: boolean}> = ({dashed}) =>
    <Box component="span" sx={{width: 24, borderTop: 2, borderTopStyle: dashed ? 'dashed' : 'solid', borderColor: 'mithraBlue.800'}}/>;

type Props = {
    nodes: DiffNode[]
    edges: Edge[]
    height: number
}
/**
 * Read-only canvas showing one change, before and after in the same picture (see `buildDiffGraph`).
 * Page scrolling is left alone (no wheel zoom); pan by dragging, zoom with the buttons.
 */
export const ChangeDiffCanvas: React.FC<Props> = ({nodes, edges, height}) => {
    const theme = useTheme();
    return <Box sx={{
        width: '100%',
        height,
        border: 1,
        borderColor: 'mithraBlue.300',
        borderRadius: 2,
        bgcolor: 'background.paper',
        '& .diff-edge-previous path': {strokeDasharray: '6 4'},
        // `!important`: the default edge colour is an inline style
        '& .diff-edge-moved path': {stroke: `${theme.palette.mithraBlue[800]} !important`, opacity: 0.55},
    }}>
        <ReactFlowProvider>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={NODE_TYPES}
                defaultEdgeOptions={{style: {stroke: theme.palette.mithraBlue[300], strokeWidth: 1.5}}}
                nodesDraggable={false}
                nodesConnectable={false}
                elementsSelectable={false}
                zoomOnScroll={false}
                preventScrolling={false}
                minZoom={0.2}
                fitView
                fitViewOptions={{padding: 0.12, maxZoom: 1}}
                proOptions={{hideAttribution: true}}
            >
                <Background variant={BackgroundVariant.Dots} gap={16} size={1}/>
                <Controls showInteractive={false}/>
                <Panel position="top-right">
                    <Stack direction="row" spacing={2} sx={{px: 1, py: 0.5, bgcolor: 'background.paper', borderRadius: 1}}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <LineSample/><Typography variant="caption" color="mithraBlue.800">Now</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <LineSample dashed/><Typography variant="caption" color="mithraBlue.800">Before</Typography>
                        </Stack>
                    </Stack>
                </Panel>
            </ReactFlow>
        </ReactFlowProvider>
    </Box>
}
